// Customer Marketplace - Consumer browsing experience (like Amazon)
// SSR-enabled: AI crawlers and search engines see real content, meta tags, and JSON-LD
// ANONYMOUS ACCESS: Marketplace is accessible to all users, including anonymous
import { readFileSync } from 'fs';

import { Box, Container, Typography } from '@mui/material';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import * as React from 'react';
import { useSelector } from 'react-redux';

import AnonymousMarketplaceHero from '../components/marketing/AnonymousMarketplaceHero';
import { ServiceGridSSR } from '../components/marketplace/ServiceGridSSR';
import type { RootState } from '../store';

// Dynamic import with ssr:false — LayoutRouter + MarketplaceContainer render client-side only
// This prevents AddressProvider/useAddress race conditions during SSR/hydration
// Head tags and JSON-LD are rendered OUTSIDE this wrapper so they appear in SSR HTML
const ClientApp = dynamic(
  () =>
    import('../components/layout/LayoutRouter').then((mod) => {
      const LayoutRouter = mod.default;
      // eslint-disable-next-line react/display-name
      return (props: { children: React.ReactNode }) => (
        <LayoutRouter>
          <div className="page-container">
            <main className="page-content">{props.children}</main>
          </div>
        </LayoutRouter>
      );
    }),
  { ssr: false, loading: () => <div /> }
);

// Lazy-load MarketplaceContainer client-side only
const MarketplaceContainer = dynamic(
  () => import('@/components/marketplace').then((mod) => mod.MarketplaceContainer),
  { ssr: false }
);

// =============================================================================
// SSR HELPER - Internal API URL resolution (same pattern as services/[id].tsx)
// =============================================================================
const ACTIVE_ENV_FILE = '/home/aithonone/public_html/.active_env';

function getApiPort(hostname: string): string {
  const host = (hostname || '').toLowerCase();
  if (host.includes('visionarynow.com')) return '3010';
  try {
    const env = readFileSync(ACTIVE_ENV_FILE, 'utf8').trim();
    if (env === 'green') return '3030';
    if (env === 'blue') return '3020';
  } catch {}
  return '3020';
}

// =============================================================================

interface HomePageProps {
  serviceCount: number;
  categories: string[];
  topServices: Array<{
    id: string;
    name: string;
    brand_name: string;
    category: string;
    description?: string;
    base_price?: number;
    price_range_min?: number;
    price_range_max?: number;
    // PR-9 tile rail (forwarded to ServiceGridSSR)
    tile_unit_basis?: 'flat' | 'per_line' | 'per_user' | 'per_seat' | 'per_site';
    tile_price_cents?: number | null;
    tile_unit_label?: string | null;
    // The API may include structured pricing fields consumed by the shared
    // SSR/hydrated card formatter. Keep the page prop open to those fields
    // instead of narrowing the payload back to denormalized min/max columns.
    [key: string]: any;
  }>;
}

export default function CustomerMarketplace({ serviceCount, categories, topServices }: HomePageProps) {
  const siteUrl = 'https://aithon.tech';
  const safeTopServices = topServices ?? [];
  const user = useSelector((state: RootState) => state.auth.user);
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => { setHydrated(true); }, []);
  const showAnonymousHero = !hydrated || !user;

  // JSON-LD: WebSite + Organization + ItemList
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Aithon',
        description: 'The AI & Tech Marketplace for enterprise technology services — where AI agents and technology partners can browse, quote, and transact.',
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Aithon',
        url: siteUrl,
        description: 'The AI & Tech Marketplace for enterprise technology services.',
        sameAs: ['https://x.com/AithonTech'],
      },
      ...(safeTopServices.length > 0
        ? [
            {
              '@type': 'ItemList',
              name: 'Featured Services',
              description: `${serviceCount} enterprise technology services available on Aithon`,
              numberOfItems: serviceCount,
              itemListElement: safeTopServices.slice(0, 10).map((s, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: s.name,
                url: `${siteUrl}/services/${s.id}`,
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <Head>
        <title>Aithon — Compare B2B Services. Real Pricing. No Sales Calls.</title>
        <meta
          name="description"
          content="Compare business services with visible pricing, side-by-side perks, and transparent competition across cloud, connectivity, security, voice, and managed IT."
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Aithon — Compare B2B Services. Real Pricing. No Sales Calls." />
        <meta
          property="og:description"
          content="Compare business services with visible pricing, side-by-side perks, and transparent competition."
        />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:image" content={`${siteUrl}/og-default.png`} />
        <meta property="og:site_name" content="Aithon" />
        <link rel="canonical" href="https://aithon.tech/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="alternate" type="application/json" href="https://aithon.tech/.well-known/agents.json" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aithon — Compare B2B Services. Real Pricing. No Sales Calls." />
        <meta
          name="twitter:description"
          content="Compare business services with visible pricing, side-by-side perks, and transparent competition."
        />
        <meta name="twitter:image" content={`${siteUrl}/og-default.png`} />
      </Head>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {showAnonymousHero && (
        <AnonymousMarketplaceHero
          title="Compare business services. Real pricing. No sales calls."
          description="When pricing is visible and providers compete in the open, the best offer wins — not the best salesperson. Stop calling three vendors for the same quote."
          browseHref="#catalog"
          secondaryLabel="For Partners & Suppliers"
          secondaryHref="/for-partners"
        />
      )}

      {/* Browse by Category — visible to users + crawlers (Amazon-style category nav) */}
      <Box id="catalog">
      {categories.length > 0 && (
        <Box component="nav" aria-label="Browse by category" sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", py: 2 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>
                Browse:
              </Typography>
              {categories.slice(0, 10).map((cat) => (
                <Box
                  key={cat}
                  component="a"
                  href={`/?category=${encodeURIComponent(cat)}`}
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.secondary",
                    textDecoration: "none",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {cat}
                </Box>
              ))}

            </Box>
          </Container>
        </Box>
      )}
      </Box>

      {/* SSR-first service grid — real visible cards for crawlers, hidden after hydration */}
      {!hydrated && safeTopServices.length > 0 && (
        <ServiceGridSSR services={safeTopServices} />
      )}

      {/* Client-side marketplace — full interactive UI after hydration */}
      {hydrated && (
        <ClientApp>
          <MarketplaceContainer />
        </ClientApp>
      )}
    </>
  );
}

export const getServerSideProps: GetServerSideProps<HomePageProps> = async (context) => {
  const hostname = context.req.headers.host || 'localhost:3011';
  const port = getApiPort(hostname);

  let serviceCount = 0;
  let categories: string[] = [];
  let topServices: HomePageProps['topServices'] = [];

  try {
    const sitemapResponse = await fetch(`http://127.0.0.1:${port}/api/public/sitemap-data`);
    if (sitemapResponse.ok) {
      const sitemapData = await sitemapResponse.json();
      const urls = Array.isArray(sitemapData?.urls) ? sitemapData.urls : [];
      serviceCount = urls.filter((u: any) => typeof u?.loc === 'string' && u.loc.includes('/services/')).length;
    }
  } catch (error) {
    console.error('[SSR] Homepage: failed to fetch sitemap data:', error);
  }

  try {
    const response = await fetch(`http://127.0.0.1:${port}/api/marketplace/services?limit=20`);
    if (response.ok) {
      const data = await response.json();
      const services = data.items || data.services || [];
      if (!serviceCount) {
        serviceCount = data.meta?.total || services.length;
      }
      topServices = services.map((s: any) => ({
        // Preserve variants, schedules, fees, and term overrides so the SSR
        // card computes the same price block as the hydrated marketplace.
        ...s,
        id: s.id,
        name: s.name,
        brand_name: s.brand_name || s.provider || '',
        is_select: s.is_select === true,
        category: s.category || '',
        description: s.description || s.short_description || '',
        base_price: s.price_cents != null ? Number(s.price_cents) : Number(s.base_price || 0),
        price_range_min: s.price_min_cents != null ? Number(s.price_min_cents) : Number(s.price_range_min || 0),
        price_range_max: s.price_max_cents != null ? Number(s.price_max_cents) : Number(s.price_range_max || 0),
        // PR-9 tile rail passthrough. Null/undefined safe: ServiceGridSSR
        // only renders the rail when tile_unit_basis is a non-flat value
        // AND tile_price_cents + tile_unit_label are present.
        tile_unit_basis: s.tile_unit_basis || 'flat',
        tile_price_cents: s.tile_price_cents ?? null,
        tile_unit_label: s.tile_unit_label ?? null,
      }));
      categories = [...new Set(services.map((s: any) => s.category).filter(Boolean))] as string[];
    }
  } catch (error) {
    console.error('[SSR] Homepage: failed to fetch marketplace services:', error);
  }


  // Defensive fallback: ensure crawlers always receive meaningful SSR content
  if (serviceCount <= 0) serviceCount = topServices.length;
  if (topServices.length === 0) {
    topServices = [
      { id: 'catalog', name: 'Business Internet Services', brand_name: 'Aithon Marketplace', category: 'Connectivity' },
      { id: 'catalog-fiber', name: 'Dedicated Fiber Services', brand_name: 'Aithon Marketplace', category: 'Connectivity' },
      { id: 'catalog-voice', name: 'UCaaS & Voice Services', brand_name: 'Aithon Marketplace', category: 'Communications' },
    ];
  }
  if (categories.length === 0) {
    categories = [...new Set(topServices.map((s) => s.category).filter(Boolean))] as string[];
  }

  // Cache for 5 minutes — content doesn't change minute to minute
  context.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=600');

  return {
    props: {
      serviceCount,
      categories,
      topServices,
    },
  };
};
